import { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';

interface CategoryData {
  category: string;
  countryAvg: number;
  usAvg: number;
  multiplier: number;
  procedureCount: number;
}

interface CountryCategoryBarChartProps {
  data: CategoryData[];
  countryName: string;
  maxCategories?: number;
}

export function CountryCategoryBarChart({
  data,
  countryName,
  maxCategories = 12,
}: CountryCategoryBarChartProps) {
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';

  const chartData = useMemo(() => {
    return [...data]
      .sort((a, b) => b.multiplier - a.multiplier)
      .slice(0, maxCategories)
      .map(d => ({
        ...d,
        // Shorten long category names
        label: d.category.length > 22 ? d.category.slice(0, 20) + '...' : d.category,
      }));
  }, [data, maxCategories]);

  const usColor = isAhp ? '#B22234' : '#ef4444';
  const countryColor = isAhp ? '#002868' : '#22c55e';
  const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  const textColor = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';
  const tooltipBg = isAhp ? (isDark ? '#0f1d2f' : '#fff') : (isDark ? '#111' : '#1a1a2e');
  const tooltipBorder = isAhp ? (isDark ? 'rgba(197,165,90,0.3)' : '#e5e7eb') : 'rgba(255,255,255,0.15)';

  if (!chartData.length) return null;

  return (
    <ResponsiveContainer width="100%" height={Math.max(320, chartData.length * 44)}>
      <BarChart
        data={chartData}
        layout="vertical"
        margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
        barGap={2}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
        <XAxis
          type="number"
          tickFormatter={(v: number) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
          tick={{ fill: textColor, fontSize: 11 }}
          axisLine={{ stroke: gridColor }}
        />
        <YAxis
          type="category"
          dataKey="label"
          width={140}
          tick={{ fill: textColor, fontSize: 11 }}
          axisLine={false}
          tickLine={false}
        />
        <RechartsTooltip
          contentStyle={{
            backgroundColor: tooltipBg,
            border: `1px solid ${tooltipBorder}`,
            borderRadius: '8px',
            color: isDark ? '#fff' : '#1B1B1B',
            fontSize: '12px',
          }}
          formatter={(value: number, name: string) => {
            const label = name === 'usAvg' ? 'US Average' : `${countryName} Average`;
            return [`$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, label];
          }}
          labelFormatter={(label: string) => label}
        />
        <Bar dataKey="countryAvg" name="countryAvg" radius={[0, 4, 4, 0]} barSize={16}>
          {chartData.map((_, i) => (
            <Cell key={i} fill={countryColor} fillOpacity={0.8} />
          ))}
        </Bar>
        <Bar dataKey="usAvg" name="usAvg" radius={[0, 4, 4, 0]} barSize={16}>
          {chartData.map((_, i) => (
            <Cell key={i} fill={usColor} fillOpacity={0.8} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
